// src/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Login.css';
import axios from 'axios';
import MessageModal from '../MessageModal';
import useAuthStore from '../stores/authStore';

const Login = () => {
  const { login, logout } = useAuthStore();

  const lastId = localStorage.getItem('lastLoginID') || '';
  const [userAddress, setuserAddress] = useState(lastId);
  const [userPass, setuserPass] = useState('');
  const [remember, setRemember] = useState(!!lastId);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalConfig, setMessageModalConfig] = useState({});

  const openModal = (config) => {
    setMessageModalConfig({
      confirmText: '閉じる',
      onConfirm: closeModal,
      onClose: closeModal,
      hideButtons: false,
      ...config,
    });
    setMessageModalOpen(true);
  };
  const closeModal = () => setMessageModalOpen(false);

  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = import.meta.env.VITE_API_URL;

  const canSubmit =
    userAddress.trim().length > 0 &&
    userPass.trim().length > 0 &&
    !submitting;

  function base64EncodeJson(obj) {
    try {
      return btoa(unescape(encodeURIComponent(JSON.stringify(obj))));
    } catch (_) {
      return '';
    }
  }

  function getNextPath() {
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    return next && next.startsWith('/') ? next : '/';
  }

  // ✅ OAuth開始（CSRFトークン不要）
  function startOAuth(provider) {
    const statePayload = {
      next: getNextPath(),
      ts: Date.now(),
      nonce: Math.random().toString(36).slice(2),
    };
    const state = base64EncodeJson(statePayload);
    sessionStorage.setItem('oauth_state', state);
    const url = `${API_URL}/api/oauth/${provider}/start?state=${encodeURIComponent(state)}`;
    window.location.href = url;
  }

  // ✅ ログイン画面表示時にストアをクリア
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isOAuthReturn = params.get('from') === 'oauth';
    if (isOAuthReturn) return;
    logout();
  }, []);

  // ✅ remember me
  useEffect(() => {
    if (remember && userAddress.trim()) {
      localStorage.setItem('lastLoginID', userAddress.trim());
    } else {
      localStorage.removeItem('lastLoginID');
    }
  }, [remember, userAddress]);

  // ✅ 通常ログイン（JWT）
  const handleLogin = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setErrorText('');

    try {
      const res = await axios.post(
        `${API_URL}/api/login/`,
        { userAddress, userPass },
      );

      if (res.data?.success) {
        const { WuserName, WuserType, access, refresh } = res.data;

        login({
          access,
          refresh,
          WuserName,
          WuserType,
        });

        const nextTo =
          WuserType === 'system' ? '/admin/dashboard' :
          WuserType === 'admin'   ? '/admin/dashboard' :
          WuserType === 'operator'   ? '/operator/dashboard'  :
          '/enduser/dashboard';

        navigate(nextTo);
      } else {
        setErrorText('メールアドレス・パスワードをご確認ください。');
      }
    } catch (error) {
      if (error.response?.status === 429) {
        const retry = parseInt(error.response?.headers?.['retry-after'] ?? '10', 10);
        setErrorText(`操作が集中しています。${retry}秒後に再試行できます。`);
      } else if (error.response?.status === 400) {
        setErrorText('メールアドレス・パスワードをご確認ください。');
      } else if (error.message === 'Network Error') {
        setErrorText('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
      } else {
        setErrorText('ログイン中にエラーが発生しました。再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div className="login-logo" style={{ marginBottom: '12px', textAlign: 'center' }}>
          <img src="/w320_180.png" alt="ロゴ" style={{ maxWidth: '280px', height: 'auto' }} />
        </div>

        <div className="login-card" role="dialog" aria-labelledby="login-title">
          <h2 id="login-title" className="login-title">ログイン</h2>

          {errorText && <div className="error-alert" role="alert">{errorText}</div>}

          <form onSubmit={handleLogin} noValidate>
            <div className="form-group">
              <label htmlFor="userAddress">メールアドレス</label>
              <input
                id="userAddress"
                name="userAddress"
                type="email"
                autoComplete="username"
                value={userAddress}
                onChange={(e) => setuserAddress(e.target.value)}
                placeholder="your@example.com"
                required
                aria-invalid={!!errorText}
              />
            </div>

            <div className="form-group">
              <label htmlFor="userPass">パスワード</label>
              <div className="password-field">
                <input
                  id="userPass"
                  name="current-password"
                  type={isPasswordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={userPass}
                  onChange={(e) => setuserPass(e.target.value)}
                  placeholder="パスワード"
                  required
                  aria-invalid={!!errorText}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setIsPasswordVisible((v) => !v)}
                  aria-label={isPasswordVisible ? 'パスワードを非表示' : 'パスワードを表示'}
                >
                  {isPasswordVisible ? '隠す' : '表示'}
                </button>
              </div>
            </div>

            <div className="form-row">
              <label className="checkbox">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                ログインIDを記憶する
              </label>
              <Link className="link" to="/forgot-password">パスワードをお忘れの方</Link>
            </div>

            <button
              type="submit"
              className={`login-button ${submitting ? 'is-loading' : ''}`}
              disabled={!canSubmit}
            >
              {submitting ? 'ログイン中…' : 'ログイン'}
            </button>
          </form>

          {/* <div className="oauth-divider"><span>または</span></div>
          <div className="oauth-buttons">
            <button type="button" className="oauth-btn oauth-google" onClick={() => startOAuth('google')} aria-label="Googleでログイン">
              Googleでログイン
            </button>
            <button type="button" className="oauth-btn oauth-line" onClick={() => startOAuth('line')} aria-label="LINEでログイン">
              LINEでログイン
            </button>
          </div>
          <div style={{ color: '#888', marginTop: 10, textAlign: 'center', fontSize: 13 }}>
            <span>GoogleまたはLINEアカウントにて新規ログインの場合、</span>
          </div>
          <div style={{ color: '#888', textAlign: 'center', fontSize: 13 }}>
            <span>メールアドレスを利用しアカウントを作成いたします</span>
          </div> */}

           <div style={{ marginTop: 10, textAlign: 'center', fontSize: 13 }}>
            アカウントをお持ちでない方は <Link to="/signup">新規登録</Link>
          </div>

        </div>
      </div>

      {messageModalOpen && <MessageModal {...messageModalConfig} />}
    </div>
  );
};

export default Login;